import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { SignOut } from "@phosphor-icons/react";
import { AuthProvider, useAuth, api } from "./lib/api";
import DetailPage from "./pages/DetailPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import SecurityPage from "./pages/SecurityPage";
import AdminPage from "./pages/AdminPage";
import StudentsPage from "./pages/StudentsPage";
import StudentDetailPage from "./pages/StudentDetailPage";
import ExamsPage from "./pages/ExamsPage";
import QuestionBankPage from "./pages/QuestionBankPage";
import AccessRequestsPage from "./pages/AccessRequestsPage";
import DashboardPage from "./pages/DashboardPage";
import PerformanceSearchPage from "./pages/PerformanceSearchPage";
import CorrectionsPage from "./pages/CorrectionsPage";
import HistoryPage from "./pages/HistoryPage";
import MobileCapturePage from "./pages/MobileCapturePage";
import CreditWalletPage from "./pages/CreditWalletPage";
import PublicInstitutionalPage from "./pages/PublicInstitutionalPage";
import LivePresenterPage from "./pages/LivePresenterPage";
import LiveParticipantPage from "./pages/LiveParticipantPage";
import LiveDisplayPage from "./pages/LiveDisplayPage";
import BulletinValidationPage from "./pages/BulletinValidationPage";
import "./App.css";

function ImpersonationBanner() {
  const { user, setUser } = useAuth();
  const active = user?.impersonation?.active;
  if (!active) return null;

  const stop = async () => {
    try {
      const response = await api.post("/admin/impersonation/stop");
      setUser(response.data.user);
      window.location.href = "/admin";
    } catch {
      window.location.reload();
    }
  };

  return (
    <div className="sticky top-[73px] z-30 border-b border-amber-500 bg-amber-50 text-amber-950 px-8 py-3" data-testid="impersonation-banner">
      <div className="max-w-[1400px] mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="font-mono-data font-bold uppercase tracking-widest text-xs">Modo de teste de permissões</div>
          <div className="text-sm mt-1">
            Você está visualizando o sistema como <strong>{user.name || user.email}</strong>. A sessão original de {user.impersonation.admin_name} permanece preservada.
          </div>
        </div>
        <button onClick={stop} className="shrink-0 border border-amber-700 bg-white px-4 py-2 font-mono-data text-xs font-bold uppercase tracking-widest hover:bg-amber-100" data-testid="stop-impersonation">
          Voltar ao administrador
        </button>
      </div>
    </div>
  );
}

function Nav() {
  const loc = useLocation();
  const { user, setUser } = useAuth();

  const linkClass = (to, exact = false) =>
    `px-3 py-2 text-xs tracking-[0.14em] uppercase border transition-colors font-mono-data ${
      (exact ? loc.pathname === to : loc.pathname === to || loc.pathname.startsWith(to + "/"))
        ? "border-foreground bg-foreground text-background"
        : "border-border text-foreground hover:border-foreground"
    }`;

  const studentTabClass = (tab) => {
    const selected = new URLSearchParams(loc.search).get("tab");
    const activeTab = ["performance", "detailed", "cohorts"].includes(selected) ? selected : "exams";
    const active = loc.pathname === `/students/${user?.student_id}` && activeTab === tab;
    return `px-2 sm:px-3 py-2 text-[10px] sm:text-xs tracking-[0.1em] sm:tracking-[0.14em] uppercase border transition-colors font-mono-data whitespace-nowrap ${
      active
        ? "border-foreground bg-foreground text-background"
        : "border-border text-foreground hover:border-foreground"
    }`;
  };

  const studentUtilityClass = (to) =>
    `px-2 sm:px-3 py-2 text-[10px] sm:text-xs tracking-[0.1em] sm:tracking-[0.14em] uppercase border transition-colors font-mono-data whitespace-nowrap ${
      loc.pathname === to
        ? "border-foreground bg-foreground text-background"
        : "border-border text-foreground hover:border-foreground"
    }`;

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(null);
    window.location.href = "/login";
  };

  return (
    <header className="border-b border-border bg-card sticky top-0 z-40">
      <div className={`max-w-[1400px] mx-auto ${user?.student_id
        ? "flex flex-col md:flex-row md:items-center md:justify-between px-4 sm:px-8 py-3 md:py-4 gap-3"
        : "flex items-center justify-between px-8 py-4 gap-4"
      }`}>
        <Link to={user?.student_id ? `/students/${user.student_id}?tab=exams` : "/dashboard"} className="flex items-baseline gap-3 no-underline text-foreground">
          <span className="font-display font-black text-2xl tracking-tighter">
            SAKI<span className="text-primary">/</span>X<span className="text-[0.7em]">m</span>
          </span>
          <span className="label-eyebrow hidden 2xl:inline">sistema de provas e controle acadêmico</span>
        </Link>
        {user && (
          <nav className={user.student_id
            ? "w-full md:w-auto flex items-center gap-1 sm:gap-2 flex-wrap"
            : "flex items-center gap-2 flex-wrap"
          } data-testid="main-nav">
            {user.student_id ? (
              <>
                <Link data-testid="nav-student-exams" to={`/students/${user.student_id}?tab=exams`} className={studentTabClass("exams")}>Provas</Link>
                <Link data-testid="nav-my-performance" to={`/students/${user.student_id}?tab=performance`} className={studentTabClass("performance")}>Desempenho geral</Link>
                <Link data-testid="nav-student-detailed-performance" to={`/students/${user.student_id}?tab=detailed`} className={studentTabClass("detailed")}>Desempenho detalhado</Link>
                <Link data-testid="nav-student-cohorts" to={`/students/${user.student_id}?tab=cohorts`} className={studentTabClass("cohorts")}>Comparação e ranking</Link>
                <Link data-testid="nav-security" to="/security" className={studentUtilityClass("/security")}>Segurança</Link>
              </>
            ) : (
              <>
            <Link data-testid="nav-dashboard" to="/dashboard" className={linkClass("/dashboard", true)}>Dashboard</Link>
            <Link data-testid="nav-performance" to="/performance" className={linkClass("/performance")}>Desempenho</Link>
            <Link
              data-testid="nav-exams"
              to="/exams"
              className={`px-3 py-2 text-xs tracking-[0.14em] uppercase border transition-colors font-mono-data ${
                loc.pathname === "/exams" && !new URLSearchParams(loc.search).get("section")
                  ? "border-foreground bg-foreground text-background"
                  : "border-border text-foreground hover:border-foreground"
              }`}
            >Provas</Link>
            <Link data-testid="nav-corrections" to="/corrections" className={linkClass("/corrections", true)}>Correções</Link>
            <Link data-testid="nav-history" to="/history" className={linkClass("/history", true)}>Histórico</Link>
            <Link data-testid="nav-questions" to="/questions" className={linkClass("/questions")}>Banco de questões</Link>
            <Link data-testid="nav-students" to="/students" className={linkClass("/students")}>Alunos</Link>
            <Link data-testid="nav-requests" to="/access-requests" className={linkClass("/access-requests")}>Acessos</Link>
            <Link data-testid="nav-security" to="/security" className={linkClass("/security")}>Segurança</Link>
            <Link data-testid="nav-mobile" to="/mobile" className={linkClass("/mobile")}>Celular</Link>
            <Link data-testid="nav-credits" to="/wallet" className={linkClass("/wallet")}>Creditos</Link>
            {(user.is_admin || (user.memberships || []).some((item) =>
              item.role === "gestor_escola" || item.role === "gestor_etapa" || item.role === "gestor_turma" || item.role === "professor"
            )) && (
              <Link data-testid="nav-admin" to="/admin" className={linkClass("/admin")}>Administração</Link>
            )}
              </>
            )}
            <div className="ml-2 flex items-center gap-2 text-xs">
              <span className="font-mono-data text-muted-foreground hidden lg:inline" data-testid="nav-user-name">
                {user.name}
              </span>
              {!user.impersonation?.active && (
                <button data-testid="nav-logout" onClick={logout}
                  className="p-2 border border-border hover:border-destructive hover:text-destructive transition-colors">
                  <SignOut size={14} weight="bold" />
                </button>
              )}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}

function SiteFooter() {
  return (
    <footer className="border-t border-border bg-card px-8 py-4 text-center text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-mono-data flex flex-wrap justify-center gap-x-4 gap-y-2">
      <span>© {new Date().getFullYear()} SAKI/Xm · Todos os direitos reservados.</span>
      <Link to="/about">Sobre</Link><Link to="/privacy">Privacidade</Link>
      <Link to="/terms">Termos</Link><Link to="/contact">Contato</Link>
    </footer>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <div className="p-16 label-eyebrow">carregando…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function StaffOnly({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <div className="p-16 label-eyebrow">carregando…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.student_id) return <Navigate to={`/students/${user.student_id}`} replace />;
  return children;
}

function HomeRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.student_id ? `/students/${user.student_id}` : "/dashboard"} replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <div className="min-h-screen flex flex-col">
            <div className="flex-1">
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/about" element={<PublicInstitutionalPage page="about" />} />
                <Route path="/privacy" element={<PublicInstitutionalPage page="privacy" />} />
                <Route path="/terms" element={<PublicInstitutionalPage page="terms" />} />
                <Route path="/contact" element={<PublicInstitutionalPage page="contact" />} />
                <Route path="/live/:code" element={<LiveParticipantPage />} />
                <Route path="/apresentar" element={<LiveDisplayPage />} />
                <Route path="/apresentar/:displayCode" element={<LiveDisplayPage />} />
                <Route path="/validar/:code" element={<BulletinValidationPage />} />
                <Route path="*" element={
                  <>
                    <Nav />
                    <ImpersonationBanner />
                    <Routes>
                      <Route path="/" element={<Protected><HomeRedirect /></Protected>} />
                      <Route path="/upload" element={<StaffOnly><Navigate to="/exams" replace /></StaffOnly>} />
                      <Route path="/history" element={<StaffOnly><HistoryPage /></StaffOnly>} />
                      <Route path="/exams" element={<StaffOnly><ExamsPage /></StaffOnly>} />
                      <Route path="/corrections" element={<StaffOnly><CorrectionsPage /></StaffOnly>} />
                      <Route path="/exams/:examId/live" element={<StaffOnly><LivePresenterPage /></StaffOnly>} />
                      <Route path="/dashboard" element={<StaffOnly><DashboardPage /></StaffOnly>} />
                      <Route path="/performance" element={<StaffOnly><PerformanceSearchPage /></StaffOnly>} />
                      <Route path="/mobile" element={<StaffOnly><MobileCapturePage /></StaffOnly>} />
                      <Route path="/wallet" element={<StaffOnly><CreditWalletPage /></StaffOnly>} />
                      <Route path="/questions" element={<StaffOnly><QuestionBankPage /></StaffOnly>} />
                      <Route path="/correction/:id" element={<Protected><DetailPage /></Protected>} />
                      <Route path="/students" element={<StaffOnly><StudentsPage /></StaffOnly>} />
                      <Route path="/students/:id" element={<Protected><StudentDetailPage /></Protected>} />
                      <Route path="/access-requests" element={<StaffOnly><AccessRequestsPage /></StaffOnly>} />
                      <Route path="/security" element={<Protected><SecurityPage /></Protected>} />
                      <Route path="/admin" element={<StaffOnly><AdminPage /></StaffOnly>} />
                    </Routes>
                  </>
                } />
              </Routes>
            </div>
            <SiteFooter />
          </div>
        </BrowserRouter>
        <Toaster position="bottom-right" toastOptions={{
          className: "font-mono-data",
          style: { borderRadius: "2px", border: "1px solid hsl(var(--border))" },
        }} />
      </AuthProvider>
    </div>
  );
}

export default App;
